import type { RoleProfile } from '@zagru/platform';
import type { IconName } from '@zagru/design-system';
import { hasRequirement, type Requirement } from '@/app/enablement/requirement';

/**
 * Navigation sections, in rail order (D-023). Home leads unlabelled; then the operation (what moves through the
 * warehouse and on the road), the business around it (customers, products, money, insight), what is built
 * (Studio, and the frozen AI area — D-026); Settings and Help sit after a divider. An area's destinations (its
 * `children`) expand inline under the area (or in a flyout when the rail is collapsed); a long area splits its
 * children into labelled groups (`NavChild.section`).
 */
export const navSections = [
  { id: 'primary', labelKey: 'shell.nav.section.primary', label: '' },
  { id: 'operations', labelKey: 'shell.nav.section.operations', label: 'Operations' },
  { id: 'business', labelKey: 'shell.nav.section.business', label: 'Business' },
  { id: 'build', labelKey: 'shell.nav.section.build', label: 'Build' },
  { id: 'system', labelKey: 'shell.nav.section.system', label: '' },
] as const;
export type NavSectionId = (typeof navSections)[number]['id'];

/**
 * W2 (spec §4): `requires` / `requiresAny` (from `Requirement`) name the app, pack or module a destination belongs to;
 * it is shown when the viewer's permission **and** that enablement hold. A child also needs its area's expression.
 */
export interface NavChild extends Requirement {
  id: string;
  /** i18n key (shell dictionary first, then the platform catalogue); `label` is the English fallback. */
  labelKey?: string;
  label: string;
  path: string;
  permissions?: string[];
  /** Feature flag key (without `feature.` prefix) that must be enabled for this destination. */
  feature?: string;
  /** Labelled group inside a long area (D-023: more than ~8 children read better in groups). */
  section?: { key: string; label: string };
  /** Other path prefixes whose pages belong to this child (a hub's record pages outside its own path, W14). */
  matchPaths?: string[];
}

export interface NavEntry extends Requirement {
  id: string;
  section: NavSectionId;
  /** i18n key under `nav.` or `shell.nav.` */
  labelKey: string;
  label: string;
  icon: IconName;
  path: string;
  /** Any of these permissions grants access (empty = everyone). Wildcards resolved by `can`. */
  permissions: string[];
  /** Feature flag key (without `feature.` prefix) that must be enabled. */
  feature?: string;
  /** Role profiles that see this entry in the primary navigation. */
  profiles: RoleProfile[];
  children?: NavChild[];
  /**
   * Extra path prefixes that belong to this area although they are mounted elsewhere (Transport's customs
   * screens live under `/customs`). Used for the active state, breadcrumb and document title.
   */
  matchPaths?: string[];
  /** Renders a rule above the entry in the rail (the first of the Settings / Help group). */
  divider?: boolean;
}

/**
 * The first child of an area the viewer may open.
 *
 * An area whose children are all mounted elsewhere — 3PL's contracts and billing live under `/browse` — has
 * no screen of its own, and its own path must lead somewhere rather than to a placeholder or a 404. Which
 * child depends on the viewer: a billing clerk with no contract grant should land on billing.
 */
export function firstPermittedChild(entry: NavEntry, can: (code: string) => boolean, isEnabled: (expr: Requirement) => boolean = () => true): string | null {
  for (const child of entry.children ?? []) {
    if (hasRequirement(child) && !isEnabled(child)) continue;
    if (!child.permissions?.length || child.permissions.some((code) => can(code))) return child.path;
  }
  return null;
}

export const allProfiles: RoleProfile[] = [
  'operator',
  'supervisor',
  'manager',
  'customer',
  'carrier',
  'driver',
  'analyst',
  'executive',
  'admin',
];
const ops: RoleProfile[] = ['operator', 'supervisor', 'manager', 'admin'];
const mgmt: RoleProfile[] = ['supervisor', 'manager', 'admin'];


/**
 * Everything an administrator can be given *only* one of — the Settings door. It must admit whoever a settings
 * screen admits, and nothing more (`navConfig.test.ts` checks both directions against the settings rail,
 * `settingsNavPermissions` in @/features/settings). `regulatory:package:manage` once went missing and a principal
 * whose only administrative grant was regulatory packages could not reach the screen built for them.
 */
export const settingsPermissions: string[] = [
  'tenancy:tenant:manage', 'tenancy:organization:manage',
  'identity:user:manage', 'identity:user:read', 'identity:role:manage', 'identity:role:read', 'identity:assignment:manage',
  'identity:api-credential:manage', 'identity:api-credential:read', 'identity:device:manage', 'identity:device:read',
  'identity:identity-provider:manage', 'identity:session:revoke',
  'configuration:set:read', 'configuration:set:edit',
  'rules:decision-point:read', 'rules:decision:evaluate', 'rules:ruleset:validate',
  'characteristic:definition:read', 'regulatory:package:manage', 'regulatory:declaration:read', 'regulatory:screening:manage',
  'localization:translation:read', 'localization:exchange-rate:read',
  'integration:connector:manage', 'integration:connector:read',
  'platform:events:read', 'platform:events:replay',
  'notification:notification:read', 'document:document:read', 'document:print-job:manage', 'document:printer:manage',
  'audit:record:read', 'controltower:sla:manage',
  'portal:config:read', 'portal:config:manage', 'portal:config:publish',
  'refdata:catalog:read', 'refdata:catalog:manage',
  'geo:place:read', 'geo:place:manage',
  // P-09: the tenant register — a platform operator's screen (ADR-0106), no tenant role holds it.
  'tenancy:tenant:provision',
  // P-12: contract templates and the clause library (Settings › Commercial).
  'contract:template:read',
  // P-28: Settings › e-Documents (SENSITIVE).
  'edocument:settings:manage',
  // P-29: Settings › U-ETDS — read to look, the (SENSITIVE) settings grant to change.
  'regulatory:trip-notification:read', 'regulatory:trip-notification:settings',
  // P-30B: Settings › Carriers (read to look, the SENSITIVE settings grant to change) and the inbound webhook endpoints.
  'parcel:carrier:read', 'parcel:carrier:settings', 'integration:webhook:read', 'integration:webhook:manage',
  // E-01: Settings › E-mail (read to look, manage to change) and the operator's e-mail usage report.
  'notification:email:read', 'notification:email:manage', 'platform:email-usage:read',
  // E-03: Settings › İYS (the consent register sync and its bulk-upload file).
  'crm:consent:manage',
];

/** The transport desk's door: whoever may open any of its planning, rating, audit or claims screens. */
export const transportArea: string[] = [
  'transport:transport:read', 'multimodal:multimodal:read', 'transport:rate:manage', 'transport:audit:manage', 'transport:claim:manage',
  'fleet:asset:read', 'transport:reservation:manage', 'multimodal:awb:manage',
];

/*
 * Labelled groups inside long areas. The key is looked up in the shell dictionary; `label` is the fallback.
 */
const grp = (key: string, label: string) => ({ key: `shell.nav.group.${key}`, label });
const G = {
  freight: grp('freight', 'Freight'),
  carriersMoney: grp('carriersMoney', 'Carriers and costs'),
  customs: grp('customs', 'Customs'),
  tasks: grp('tasks', 'Tasks'),
  processes: grp('processes', 'Processes'),
  people: grp('people', 'People and papers'),
  sites: grp('sites', 'Sites'),
  fleet: grp('fleet', 'Fleet & equipment'),
  equipment: grp('equipment', 'Equipment'),
  directory: grp('directory', 'Directory'),
  sales: grp('sales', 'Sales'),
  catalog: grp('catalog', 'Catalog'),
  pricing: grp('pricing', 'Pricing'),
  analysis: grp('analysis', 'Analysis'),
  controlTower: grp('controlTower', 'Control tower'),
  families: grp('families', 'What you build'),
  governance: grp('governance', 'Governance'),
};

/**
 * Primary navigation (D-023, ROADMAP §3). Order matters: it is the rail's reading order. Fourteen areas plus
 * Settings and Help (and the frozen AI area, D-026). Every destination names the permission its own screen
 * requires, so a viewer sees exactly the destinations they can open; an area admits whoever may open any of
 * its destinations.
 *
 * The same record type lives in exactly one place: warehouse orders under Outbound, sales orders under
 * Orders; shipments under Transport; the automation registry under Sites & equipment; contracts and billing
 * under Finance. Moved paths keep working — they are the same routes, only the menu moved (see
 * `INFORMATION_ARCHITECTURE.md`).
 */
export const primaryNav: NavEntry[] = [
  {
    id: 'today',
    section: 'primary',
    labelKey: 'hub.today.label',
    label: 'Home',
    icon: 'home',
    path: '/today',
    permissions: [],
    profiles: allProfiles,
    // Wave 4 H-01: the Bugün hub — Özet, Dikkat gerektirenler, Görevlerim, Onaylarım are its lenses.
  },

  /* ---- Operations ---- */
  {
    id: 'orders',
    section: 'operations',
    labelKey: 'nav.orders',
    label: 'Orders',
    icon: 'receipt',
    path: '/orders',
    // The area admits whoever may open any of its destinations (the channel and return desks included).
    permissions: ['fulfillment:order:read', 'fulfillment:channel-update:read', 'fulfillment:channel-update:retry', 'fulfillment:channel:manage', 'fulfillment:return:authorize', 'marketplace:pricing:read', 'marketplace:settlement:read', 'marketplace:application:read', 'outbound:order:read', 'inbound:expected-receipt:read', 'outbound:transfer:read'],
    profiles: [...ops, 'customer', 'analyst'],
    children: [
      // Wave 4 H-01: the Siparişler hub — sales, marketplace, e-commerce, warehouse outbound, purchase, transfers,
      // returns, backorders, channel sync and channels are its lenses. The setup pages below stay until their hubs.
      { id: 'orders-hub', labelKey: 'hub.orders.label', label: 'Orders', path: '/orders', permissions: ['fulfillment:order:read', 'outbound:order:read', 'inbound:expected-receipt:read', 'outbound:transfer:read'] },
      // L-10a (D-323): how order lines find a warehouse when no rule or promise decided.
      { id: 'oms-sourcing', labelKey: 'nav.salesOrders.sourcing', label: 'Sourcing policies', path: '/oms/sourcing', permissions: ['fulfillment:order:read', 'fulfillment:channel:manage'] },
      { id: 'marketplace-applications', labelKey: 'marketplace.applications.title', label: 'Integrator applications', path: '/oms/marketplaces/applications', permissions: ['marketplace:application:read'], requires: 'app:category:marketplace' },
    ],
  },
  {
    // Wave 4 H-02: the Depo operasyonu hub — receiving, quality, putaway, waves, packing, shipping desk, loading,
    // replenishment, cross-dock, counts, value-added services, tasks, queues and supervision are its lenses. The setup
    // and tool pages of the old Inbound / Outbound areas stay below.
    id: 'warehouse',
    section: 'operations',
    labelKey: 'hub.warehouse.label',
    label: 'Warehouse',
    icon: 'warehouse',
    path: '/warehouse',
    permissions: ['inbound:receipt:read', 'inbound:receipt:receive', 'outbound:wave:manage', 'outbound:pack:execute', 'outbound:shipment:confirm', 'outbound:load:execute', 'outbound:replenishment:manage', 'inventory:count:plan', 'threepl:work-order:read', 'task:task:read', 'task:task:supervise'],
    profiles: [...ops, 'customer', 'analyst'],
    children: [
      { id: 'warehouse-hub', labelKey: 'hub.warehouse.label', label: 'Warehouse', path: '/warehouse', permissions: ['inbound:receipt:read', 'inbound:receipt:receive', 'outbound:wave:manage', 'outbound:pack:execute', 'outbound:shipment:confirm', 'outbound:load:execute', 'outbound:replenishment:manage', 'inventory:count:plan', 'threepl:work-order:read', 'task:task:read', 'task:task:supervise'] },
      { id: 'inbound-putaway-rules', labelKey: 'shell.nav.inbound.putawayRules', label: 'Putaway rules', path: '/inbound/putaway-rules', permissions: ['inbound:receipt:read'] },
      { id: 'outbound-job-rules', labelKey: 'outboundJobs.rules.title', label: 'Job rules', path: '/outbound/job-rules', permissions: ['outbound:job:manage'] },
      { id: 'outbound-pickup-schedules', labelKey: 'shell.nav.outbound.pickupSchedules', label: 'Pickup schedules', path: '/outbound/pickup-schedules', permissions: ['outbound:order:read', 'outbound:pickup-schedule:manage'] },
      // Every reason the ship confirmation refused an order; the shipping desk's work, under its permission.
      { id: 'outbound-dispatch-refusals', labelKey: 'shell.nav.outbound.dispatchRefusals', label: 'Dispatch exceptions', path: '/outbound/shipping-exceptions', permissions: ['outbound:shipment:confirm'] },
    ],
  },
  {
    // Wave 4 H-02: the Stok hub — balances, handling units, lots, movements, adjustments, reservations, bonded stock
    // and serial notifications are its lenses; the scanner lookup, the serial register and count entry stay pages.
    id: 'inventory',
    section: 'operations',
    labelKey: 'hub.stock.label',
    label: 'Stock',
    icon: 'boxes',
    path: '/stock',
    permissions: ['inventory:balance:read', 'inventory:balance:adjust', 'inventory:count:plan', 'inventory:count:execute'],
    profiles: [...ops, 'customer', 'analyst'],
    children: [
      { id: 'stock-hub', labelKey: 'hub.stock.label', label: 'Stock', path: '/stock', permissions: ['inventory:balance:read', 'inventory:balance:adjust'] },
      // D-016: the scanner lookup is a page; the palette's `sku:` scope lands here too.
      { id: 'inventory-lookup', labelKey: 'shell.nav.inventory.lookup', label: 'Stock lookup', path: '/inventory/lookup', permissions: ['inventory:balance:read'] },
      { id: 'inventory-serials', labelKey: 'shell.nav.inventory.serials', label: 'Serial register', path: '/inventory/serials', permissions: ['inventory:balance:read'] },
      // P-22 (04 #14): counting on the web for a site without RF devices — the counter's way in, not the planner's.
      { id: 'inventory-count-entry', labelKey: 'shell.nav.inventory.countEntry', label: 'Count entry', path: '/inventory/count-entry', permissions: ['inventory:count:execute'] },
    ],
  },
  {
    id: 'transport',
    section: 'operations',
    labelKey: 'nav.transport',
    label: 'Transport',
    icon: 'route',
    path: '/shipments',
    permissions: ['shipment:shipment:read', ...transportArea, 'regulatory:declaration:read', 'regulatory:compliance:read', 'regulatory:trip-notification:read', 'parcel:carrier:read'],
    profiles: [...ops, 'customer', 'analyst'],
    // Multimodal's screens are mounted at `/multimodal` and customs at `/customs`; the area has to say so or the
    // active state, the breadcrumb and the title would disagree with the address bar.
    matchPaths: ['/multimodal', '/customs'],
    children: [
      // Wave 4 H-03: the Sevkiyatlar hub — shipments, road loads, parcels, multimodal, dispatch board, routes, tracking,
      // carrier quotes and transport notifications are its lenses; the tools and registries below stay pages.
      { id: 'shipments-hub', labelKey: 'hub.shipments.label', label: 'Shipments', path: '/shipments', permissions: ['shipment:shipment:read', ...transportArea] },
      { id: 'transport-spot-quotes', labelKey: 'shell.nav.transport.spotQuotes', label: 'Spot quotes', path: '/transport/spot-quotes', permissions: transportArea, feature: 'transport', section: G.freight },
      // P-29: trip notifications to the Ministry of Transport (U-ETDS) for dispatched domestic road shipments and loads.
      // P-30B: labels and return codes booked at the Turkish parcel carriers, tracked until delivered.
      // Bookings, containers and transport documents are how freight moves across modes (NAV-001).
      // CGX-007: master AWB stock, a multimodal register (mounted at `/multimodal`, already in matchPaths).
      { id: 'transport-awb-stock', labelKey: 'shell.nav.transport.awbStock', label: 'AWB stock', path: '/multimodal/awb-stock', permissions: ['multimodal:multimodal:read', 'multimodal:awb:manage'], feature: 'transport', section: G.freight, requires: 'module:multimodal' },
      // P-09: the port and terminal register sea and air quotes convert against (mounted at `/multimodal`).
      { id: 'transport-ports', labelKey: 'shell.nav.transport.ports', label: 'Ports and terminals', path: '/multimodal/ports', permissions: ['multimodal:multimodal:read', 'multimodal:reference:manage'], feature: 'transport', section: G.freight, requires: 'module:multimodal' },
      { id: 'transport-rates', labelKey: 'shell.nav.transport.rates', label: 'Rate tables', path: '/transport/rates', permissions: ['transport:rate:manage'], feature: 'transport', section: G.carriersMoney },
      { id: 'transport-vehicle-types', labelKey: 'shell.nav.transport.vehicleTypes', label: 'Vehicle types', path: '/transport/vehicle-types', permissions: transportArea, feature: 'transport', section: G.carriersMoney },
      // CGX-003: space held ahead of a shipment, in any mode.
      { id: 'transport-reservations', labelKey: 'shell.nav.transport.reservations', label: 'Capacity reservations', path: '/transport/reservations', permissions: ['transport:transport:read', 'transport:reservation:manage'], feature: 'transport', section: G.carriersMoney },
      // Wave 4 H-04: the Gümrük ve ticaret hub — files, declarations, bonded, compliance, guarantees, duty risk.
      { id: 'trade-hub', labelKey: 'hub.trade.label', label: 'Customs and trade', path: '/trade', matchPaths: ['/customs'], permissions: ['regulatory:broker-file:read', 'regulatory:declaration:read', 'regulatory:bonded:read', 'regulatory:compliance:read', 'regulatory:duty:read', 'regulatory:screening:read'], section: G.customs, requires: 'module:customs' },
    ],
  },
  {
    id: 'yard',
    section: 'operations',
    labelKey: 'nav.yardDocks',
    label: 'Yard & docks',
    icon: 'dock',
    path: '/yard',
    // The gate desk's grant alone must still reach the area (a gate guard need not read the whole yard).
    permissions: ['yard:yard:read', 'yard:gate:execute'],
    // W2: the yard is a module (`platform.modules.yard.enabled`); every destination inherits it.
    requires: 'module:yard',
    profiles: ops,
    // Wave 4 H-03: the Saha hub — gate, appointments, dock board and yard map are its lenses.
  },
  {
    id: 'sites',
    section: 'operations',
    labelKey: 'shell.nav.sites',
    label: 'Sites & equipment',
    icon: 'warehouse',
    path: '/facilities',
    permissions: [],
    profiles: allProfiles,
    matchPaths: ['/equipment', '/automation', '/transport/fleet', '/fleet'],
    children: [
      // Wave 4 H-05: the Tesisler ve kaynaklar hub (warehouses, places, equipment, printers, workforce).
      { id: 'facilities-hub', labelKey: 'hub.facilities.label', label: 'Facilities and resources', path: '/facilities' },
      /*
       * P-18 (01 §G21): "Fleet & equipment" — one area where the two fleet lists (`/transport/fleet`, `/equipment`) were.
       * The unified list keeps every other module's assets (yard, automation, warehouse equipment, handhelds) as its
       * register segment, so it opens with the register's read as well as fleet's. The old paths redirect.
       */
      // Wave 4 H-03: the Filo hub — dashboard, assets, drivers, maintenance, fuel, incidents, costs, compliance, live map.
      { id: 'fleet-hub', labelKey: 'hub.fleet.label', label: 'Fleet', path: '/fleet', permissions: ['fleet:asset:read'], requires: 'module:fleet' },
      // P-34 (D-136): where the tracked fleet is now.
      // P-19 (01 §G16–G17): fuel, typed or imported from a fuel card, and what each asset costs per km.
      // P-19 (01 §G18): tyres, fitted to vehicles, with tread readings.
      { id: 'fleet-tyres', labelKey: 'shell.nav.fleet.tyres', label: 'Tyres', path: '/fleet/tyres', permissions: ['fleet:asset:read'], section: G.fleet, requires: 'module:fleet' },
    ],
  },

  /* ---- Business ---- */
  {
    id: 'partners',
    section: 'business',
    labelKey: 'shell.nav.partners',
    label: 'Customers & partners',
    icon: 'users',
    path: '/partners',
    permissions: [],
    profiles: allProfiles,
    // CRM and quotes are mounted at `/crm` and `/cpq`; organisations under Browse.
    matchPaths: ['/crm', '/cpq', '/browse/organizations', '/browse/contacts'],
    children: [
      // Wave 4 H-05: the Müşteriler ve ortaklar hub; the tools below stay pages.
      { id: 'partners-hub', labelKey: 'hub.partners.label', label: 'Customers and partners', path: '/partners' },
      { id: 'organizations', labelKey: 'shell.nav.partners.organizations', label: 'Organisations', path: '/browse/organizations', permissions: ['tenancy:tenant:manage', 'tenancy:organization:manage'], section: G.directory },
      { id: 'crm-follow-ups', labelKey: 'shell.nav.crm.followUps', label: 'My follow-ups', path: '/crm/follow-ups', permissions: ['crm:activity:read'], section: G.sales, requires: 'module:crm' },
      // L-10c (D-325): the weighted pipeline forecast.
      { id: 'crm-forecast', labelKey: 'shell.nav.crm.forecast', label: 'Pipeline forecast', path: '/crm/opportunities/forecast', permissions: ['crm:opportunity:read'], section: G.sales, requires: 'module:crm' },
      // P-14: data subject requests (KVKK art. 11 / GDPR), next to Cases.
      { id: 'crm-privacy-requests', labelKey: 'shell.nav.crm.privacyRequests', label: 'Data subject requests', path: '/crm/privacy-requests', permissions: ['crm:privacy:read', 'crm:privacy:manage'], section: G.sales },
      /*
       * P-13 (02 §E6): the customer portal's settings, reachable from where customers are managed. The screen stays in
       * Settings (and `/settings/portal` is not in `matchPaths`, so Settings stays the active area there); the portal
       * itself never appears in the internal rail.
       */
      { id: 'customer-portal', labelKey: 'shell.nav.partners.customerPortal', label: 'Customer portal', path: '/settings/portal', permissions: ['portal:config:read'], section: G.sales },
    ],
  },
  {
    id: 'catalog',
    section: 'business',
    labelKey: 'shell.nav.products',
    label: 'Products & pricing',
    icon: 'tag',
    path: '/products',
    permissions: [],
    profiles: [...mgmt, 'analyst', 'executive'],
    matchPaths: ['/pricing', '/browse/products'],
    children: [
      // The item master (SKUs, packaging, units) every warehouse screen reads.
      // Wave 4 H-05: the Ürünler hub; the asset library and the price explorer stay pages.
      { id: 'products-hub', labelKey: 'hub.products.label', label: 'Products', path: '/products' },
      // L-10b (D-324): the shared asset library and the product-content translation work.
      { id: 'catalog-assets', labelKey: 'shell.nav.catalog.assets', label: 'Asset library', path: '/catalog/assets', permissions: ['catalog:product:read'], section: G.catalog },
      { id: 'pricing-explain', labelKey: 'nav.pricing.explain', label: 'Price explorer', path: '/pricing/explain', permissions: ['pricing:price:resolve'], section: G.pricing },
    ],
  },
  {
    id: 'billing',
    section: 'business',
    labelKey: 'shell.nav.finance',
    label: 'Finance',
    icon: 'briefcase',
    path: '/finance',
    permissions: ['billing:charge:read', 'billing:invoice:run', 'billing:cost:read', 'billing:settlement:manage', 'billing:receivable:read', 'threepl:contract:read', 'threepl:contract:manage', 'contract:agreement:read', 'edocument:submission:read', 'marketplace:settlement:read', 'transport:audit:manage'],
    profiles: [...mgmt, 'analyst', 'executive'],
    matchPaths: ['/browse/contracts', '/3pl', '/contracts', '/finance'],
    children: [
      // Wave 4 H-04: the Finans hub — billing, settlements, costs, collections and e-documents are its lenses; contracts
      // and the billing tools below stay pages.
      { id: 'finance-hub', labelKey: 'hub.finance.label', label: 'Finance', path: '/finance', permissions: ['billing:charge:read', 'billing:cost:read', 'billing:receivable:read', 'marketplace:settlement:read', 'transport:audit:manage', 'edocument:submission:read'] },
      // Running a billing period and pricing a value-added service are things you do to a 3PL contract.
      { id: 'threepl-billing-operations', labelKey: 'shell.nav.threepl.billingOperations', label: 'Billing operations', path: '/browse/billing/operations', permissions: ['billing:invoice:run'] },
      { id: 'threepl-vas', labelKey: 'shell.nav.threepl.vas', label: 'Value-added services', path: '/browse/contracts/vas-definitions', permissions: ['threepl:contract:manage'] },
    ],
  },
  {
    id: 'reports',
    section: 'business',
    labelKey: 'shell.nav.insights',
    label: 'Insights',
    icon: 'bar-chart-3',
    path: '/insights',
    permissions: ['analytics:report:read', 'semantic:query:execute', 'controltower:exception:read'],
    profiles: [...mgmt, 'customer', 'analyst', 'executive'],
    matchPaths: ['/control-tower'],
    children: [
      // Wave 4 H-05: the İçgörüler hub (dashboards, reports, control tower, processes, documents).
      { id: 'insights-hub', labelKey: 'hub.insights.label', label: 'Insights', path: '/insights' },
    ],
  },

  /* ---- Build ---- */
  {
    id: 'studio',
    section: 'build',
    labelKey: 'nav.studio',
    label: 'Studio',
    icon: 'blocks',
    path: '/studio',
    // Wave 5 §3.11: the Integration Studio is a Studio lens for Zagru operators (`integration:studio:admin`).
    permissions: ['metadata:artifact:read', 'studio:change-set:read', 'studio:proposal:read', 'integration:studio:admin'],
    profiles: ['supervisor', 'manager', 'admin'],
    /*
     * D-024: the artifact families first — what you build — then the governance lifecycle (change sets,
     * approvals, proposals), which are places you go inside Studio having decided to change the configuration.
     * A family opens its leading kind's list; the family's other kinds are one click away from the catalogue
     * ("All kinds") and from the kind list's own switcher.
     */
    children: [
      // Wave 4 H-06: the Studio hub — home (kinds catalogue), change sets, approvals, proposals, AI operations. The kind
      // pages are pinnable views of home rather than menu entries.
      { id: 'studio-hub', labelKey: 'hub.studio.label', label: 'Studio', path: '/studio' },
      // Wave 4 W14 (spec §10.5): the AI configuration pages are Studio tools; the AI area keeps the assistant alone.
      { id: 'ai-providers', labelKey: 'ai.nav.providers', label: 'Providers & policy', path: '/ai/operations/providers', permissions: ['ai:agent:manage', 'ai:policy:manage'], requires: 'module:ai' },
      { id: 'ai-knowledge', labelKey: 'ai.nav.knowledge', label: 'Knowledge sources', path: '/ai/operations/knowledge', permissions: ['ai:agent:manage', 'ai:policy:manage'], requires: 'module:ai' },
    ],
  },
  /* Frozen (D-026): kept as it was, one entry, hidden by its own permissions and feature flag. */
  {
    id: 'ai',
    section: 'build',
    labelKey: 'nav.aiOperations',
    label: 'AI Operations',
    icon: 'sparkles',
    path: '/ai',
    permissions: ['ai:conversation:use', 'ai:action:approve', 'ai:agent:manage', 'ai:policy:manage'],
    feature: 'aiOperations',
    profiles: [...mgmt, 'analyst', 'executive'],
    children: [
      { id: 'ai-conversations', labelKey: 'ai.nav.assistant', label: 'Assistant', path: '/ai', permissions: ['ai:conversation:use', 'ai:action:approve'] },
    ],
  },

  /* ---- After a divider: administration and help ---- */
  // Wave 4 W5 (spec §5): the App Center — integrations and packs are installed here.
  {
    id: 'apps',
    section: 'system',
    divider: true,
    labelKey: 'apps.center.title',
    label: 'Apps',
    icon: 'layout-grid',
    path: '/apps',
    permissions: ['app:install:manage', 'integration:connector:read', 'metadata:pack:manage'],
    profiles: ['admin', 'manager', 'supervisor'],
  },
  {
    id: 'settings',
    section: 'system',
    divider: true,
    labelKey: 'nav.settings',
    label: 'Settings',
    icon: 'settings',
    path: '/settings',
    matchPaths: ['/localization'],
    /*
     * Everything an administrator can be given *only* one of. It must admit whoever its children admit, or a
     * screen built for exactly one kind of administrator becomes unreachable by them (`navConfig.test.ts`
     * checks both directions against `settingsNavPermissions` in @/features/settings).
     */
    permissions: settingsPermissions,
    profiles: ['manager', 'admin'],
  },
];

export interface BrowseModule extends Requirement {
  id: string;
  label: string;
  description: string;
  group: 'Setup' | 'Operate' | 'Administer';
  context: string;
  path: string;
  permissions: string[];
  icon: IconName;
}

/** Classic deterministic module list ("Browse") for power users. */
export const browseModules: BrowseModule[] = [
  {
    id: 'organization',
    label: 'Organizations',
    description: 'Legal entities, sites and reporting hierarchy.',
    group: 'Setup',
    context: 'organization',
    path: '/browse/organizations',
    permissions: ['tenancy:tenant:manage', 'tenancy:organization:manage'],
    icon: 'layers',
  },
  {
    id: 'party',
    label: 'Parties',
    description: 'Customers, suppliers, carriers and operators.',
    group: 'Setup',
    context: 'party',
    path: '/browse/parties',
    permissions: [],
    icon: 'briefcase',
  },
  {
    id: 'product',
    label: 'Products',
    description: 'SKUs, packaging, units and product attributes.',
    group: 'Setup',
    context: 'product',
    path: '/browse/products',
    permissions: [],
    icon: 'package',
  },
  {
    id: 'warehouse',
    label: 'Warehouses & locations',
    description: 'Sites, zones, bins, docks and staging lanes.',
    group: 'Setup',
    context: 'warehouse',
    path: '/browse/warehouses',
    permissions: [],
    icon: 'building',
  },
  {
    id: 'inventory',
    label: 'Inventory',
    description: 'Balances, handling units, lots and movements.',
    group: 'Operate',
    context: 'inventory',
    path: '/inventory/balances',
    permissions: ['inventory:balance:read'],
    icon: 'boxes',
  },
  {
    id: 'inbound',
    label: 'Inbound',
    description: 'Expected receipts, receiving and quality control.',
    group: 'Operate',
    context: 'inbound',
    path: '/inbound/expected-receipts',
    permissions: ['inbound:receipt:read'],
    icon: 'inbox',
  },
  {
    id: 'outbound',
    label: 'Outbound',
    description: 'Orders, waves, packing and shipping.',
    group: 'Operate',
    context: 'outbound',
    path: '/outbound/orders',
    permissions: ['outbound:order:read'],
    icon: 'truck',
  },
  {
    id: 'crm',
    label: 'Customers',
    description: 'Leads, opportunities, cases and account 360.',
    group: 'Operate',
    context: 'crm',
    path: '/crm/leads',
    permissions: ['crm:lead:read'],
    requires: 'module:crm',
    icon: 'handshake',
  },
  {
    id: 'task',
    label: 'Tasks',
    description: 'Work queues, assignments and execution status.',
    group: 'Operate',
    context: 'task',
    path: '/tasks',
    permissions: ['task:task:read'],
    icon: 'clipboard',
  },
  {
    id: 'labor',
    label: 'Labor',
    description: 'Workers, productivity and workforce coverage.',
    group: 'Operate',
    context: 'labor',
    path: '/workforce',
    permissions: ['labor:worker:read'],
    icon: 'users',
  },
  {
    id: 'yard',
    label: 'Yard',
    description: 'Appointments, dock doors, trailers and yard map.',
    group: 'Operate',
    context: 'yard',
    path: '/yard/appointments',
    permissions: ['yard:yard:read'],
    requires: 'module:yard',
    icon: 'dock',
  },
  {
    id: 'transport',
    label: 'Transport',
    description: 'Loads, tenders, carriers and live tracking.',
    group: 'Operate',
    context: 'transport',
    path: '/transport/loads',
    permissions: ['transport:transport:read'],
    icon: 'route',
  },
  {
    id: 'workflow',
    label: 'Workflows',
    description: 'Instances, approvals and workflow definitions.',
    group: 'Administer',
    context: 'workflow',
    path: '/browse/workflows',
    permissions: ['workflow:instance:read'],
    icon: 'workflow',
  },
  {
    id: 'billing',
    label: 'Billing',
    description: 'Charges, invoices and billable events.',
    group: 'Administer',
    context: 'billing',
    path: '/browse/billing',
    permissions: ['billing:charge:read'],
    icon: 'file-text',
  },
  {
    id: 'threepl',
    label: '3PL contracts',
    description: 'Contracts, rate cards and customer billing.',
    group: 'Administer',
    context: 'threepl',
    path: '/browse/contracts',
    permissions: ['threepl:contract:read'],
    icon: 'file',
  },
  {
    id: 'integration',
    label: 'Integration',
    description: 'Connectors, messages and webhooks.',
    group: 'Administer',
    context: 'integration',
    path: '/settings/integrations',
    permissions: ['integration:connector:read', 'integration:connector:manage'],
    icon: 'plug',
  },
  /*
   * Documents cut across every area — a bill of lading belongs to a shipment, a label to a pack unit, a
   * print job to whichever desk is standing at the printer. There is no one area whose tab strip it
   * belongs in, and it is not somewhere an operator starts their day, so it goes where the other
   * cross-cutting registers go rather than becoming a twenty-second row in the rail (NAV-001).
   */
  {
    id: 'documents',
    label: 'Documents & printing',
    description: 'Generated documents, labels and the print queue.',
    group: 'Operate',
    context: 'document',
    path: '/documents',
    permissions: ['document:document:read', 'document:print-job:manage'],
    icon: 'file-text',
  },
  {
    id: 'audit',
    label: 'Audit log',
    description: 'Trace changes and operational events.',
    group: 'Administer',
    context: 'platform',
    path: '/settings/audit',
    permissions: ['audit:record:read'],
    icon: 'shield',
  },
];

/** Landing page per role profile. */
export const landingPage: Record<RoleProfile, string> = {
  operator: '/tasks',
  supervisor: '/today',
  manager: '/today',
  customer: '/portal', // the customer profile lands in its own experience, never the internal shell
  carrier: '/carrier', // P-32: the carrier portal, likewise its own shell
  driver: '/driver-app', // P-33: a driver works in the phone app; the web says so
  analyst: '/reports',
  executive: '/today',
  admin: '/today',
};

/** Default density per profile (doc 21: RoleProfile selects density). */
export const defaultDensity: Record<RoleProfile, 'comfortable' | 'compact' | 'touch'> = {
  operator: 'touch',
  supervisor: 'compact',
  manager: 'comfortable',
  customer: 'comfortable',
  carrier: 'comfortable',
  driver: 'comfortable',
  analyst: 'compact',
  executive: 'comfortable',
  admin: 'comfortable',
};

export interface NavResolveOptions {
  profile: RoleProfile;
  can: (permission: string) => boolean;
  isFeatureEnabled?: (key: string) => boolean;
  /** W2: whether an enablement expression holds for the tenant (`useEnablementCheck`); everything is on when absent. */
  isEnabled?: (expr: Requirement) => boolean;
}

/** An entry's or child's own expression, or `undefined` for a core destination. */
export function navRequirement(item: Requirement): Requirement | undefined {
  return hasRequirement(item) ? { requires: item.requires, requiresAny: item.requiresAny } : undefined;
}

function allowed(permissions: string[] | undefined, can: (p: string) => boolean): boolean {
  if (!permissions || permissions.length === 0) return true;
  return permissions.some((p) => can(p));
}

/** Filters the primary navigation for a role profile, permissions and feature flags. */
export function resolvePrimaryNav({
  profile,
  can,
  isFeatureEnabled = () => true,
  isEnabled = () => true,
}: NavResolveOptions): NavEntry[] {
  const on = (item: Requirement) => !hasRequirement(item) || isEnabled(item);
  return primaryNav
    .filter((e) => e.profiles.includes(profile))
    .filter((e) => allowed(e.permissions, can))
    .filter((e) => !e.feature || isFeatureEnabled(e.feature))
    // W2: permission ∧ enablement — the area's own expression, then each destination's.
    .filter((e) => on(e))
    .map((e) => ({ ...e, children: e.children?.filter((c) => allowed(c.permissions, can) && (!c.feature || isFeatureEnabled(c.feature)) && on(c)) }))
    // An area whose every destination is hidden from this viewer has nowhere to lead.
    .filter((e) => !e.children || e.children.length > 0);
}

export function resolveBrowseModules(can: (permission: string) => boolean, isEnabled: (expr: Requirement) => boolean = () => true): BrowseModule[] {
  return browseModules.filter((m) => allowed(m.permissions, can) && (!hasRequirement(m) || isEnabled(m)));
}

/** Finds the nav entry (and child) matching a pathname for breadcrumbs / active state. */
const under = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * The entry a path belongs to: the **longest** match; on a tie the entry declared first (the promoted one).
 *
 * Areas overlap by design — `/transport/shipments` is both Transport's and the promoted Shipments entry's,
 * `/automation` is both Equipment and Automation — so the longest prefix decides, and the primary group,
 * declared first, wins an exact tie.
 */
export function findNavByPath(pathname: string): { entry: NavEntry; child?: NavChild } | null {
  let best: { entry: NavEntry; child?: NavChild } | null = null;
  let bestLength = -1;
  for (const entry of primaryNav) {
    const own = [entry.path, ...(entry.matchPaths ?? [])].filter((p) => under(pathname, p)).sort((a, b) => b.length - a.length)[0];
    // The longest matching child wins (an "overview" child may share the area's own path), and it counts
    // towards the area's specificity: `/automation/commands` is Automation's, although Equipment also lives at `/automation`.
    // W14: a hub's tool page outside the area's paths (`/inventory/serials` under Stok) is matched by its child alone.
    let child: NavChild | undefined;
    let childLength = -1;
    for (const candidate of entry.children ?? []) {
      const hit = [candidate.path, ...(candidate.matchPaths ?? [])].filter((p) => under(pathname, p)).sort((a, b) => b.length - a.length)[0];
      if (hit !== undefined && hit.length > childLength) {
        child = candidate;
        childLength = hit.length;
      }
    }
    if (own === undefined && !child) continue;
    const length = Math.max(own?.length ?? -1, childLength);
    if (length > bestLength) {
      bestLength = length;
      best = child ? { entry, child } : { entry };
    }
  }
  return best;
}

/** Where an area lands when opened from the rail: its first reachable child, else its own path. */
export function entryLandingPath(entry: NavEntry): string {
  return entry.children?.[0]?.path ?? entry.path;
}

/**
 * Wave 4 W14 (spec §10.5): the sidebar has no second level. Each hub child (`*-hub`) of an area is its own sidebar
 * entry; the area's other destinations are the tools of the hub they follow (tools listed before the first hub belong
 * to it), shown in that hub's header. An area without a hub is one entry whose tools stay in the palette.
 */
export interface NavHubGroup {
  /** The sidebar item id: the area's id for its first group (badges, shortcuts), the hub child's id after that. */
  itemId: string;
  hub?: NavChild;
  tools: NavChild[];
}

export function navHubGroups(entry: NavEntry): NavHubGroup[] {
  const groups: NavHubGroup[] = [];
  for (const child of entry.children ?? []) {
    const last = groups[groups.length - 1];
    if (child.id.endsWith('-hub')) {
      if (last && !last.hub) last.hub = child;
      else groups.push({ itemId: groups.length ? child.id : entry.id, hub: child, tools: [] });
    } else if (last) last.tools.push(child);
    else groups.push({ itemId: entry.id, tools: [child] });
  }
  return groups.length ? groups : [{ itemId: entry.id, tools: [] }];
}
